import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBranches, createBranch, updateBranch, getBranchMode } from '../../api/branches';
import { usersAPI } from '../../api/users';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import {
  Building2,
  Plus,
  Edit2,
  Users,
  Package,
  LayoutGrid,
  List,
  MapPin,
  Phone,
  Mail,
  CheckCircle,
  XCircle,
  Search,
  ChevronRight
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const BranchesPage = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(localStorage.getItem('branchesViewMode') || 'grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branchMode, setBranchMode] = useState(null);
  const [availableManagers, setAvailableManagers] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Check if user can manage branches (retail_admin+)
  const canManageBranches = ['super_admin', 'system_admin', 'retail_admin'].includes(
    currentUser?.role
  );

  useEffect(() => {
    localStorage.setItem('branchesViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    fetchBranches();
    fetchBranchMode();
    fetchAvailableManagers();
  }, [searchQuery, pagination.page]);

  const fetchBranchMode = async () => {
    try {
      const response = await getBranchMode();
      setBranchMode(response.data);
    } catch (error) {
      console.error('Error fetching branch mode:', error);
    }
  };

  const fetchBranches = async () => {
    try {
      setLoading(true);
      const response = await getBranches({
        page: pagination.page,
        limit: pagination.limit,
        search: searchQuery,
        sortBy: 'created_at',
        sortOrder: 'DESC'
      });
      setBranches(response.data || []);
      if (response.pagination) {
        setPagination((prev) => ({
          ...prev,
          total: response.pagination.total,
          totalPages: response.pagination.totalPages
        }));
      }
    } catch (error) {
      toast.error('Failed to load branches');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableManagers = async () => {
    try {
      const response = await usersAPI.getAllUsers();
      // API returns { success: true, data: users[] }, so access .data or use the response directly if it's already an array
      const users = Array.isArray(response.data) ? response.data : response.data || response || [];
      // Filter to admin/staff who can be managers
      const managers = users.filter(
        (u) => ['admin', 'retail_admin', 'branch_admin'].includes(u.role) && !u.is_locked
      );
      setAvailableManagers(managers);
    } catch (error) {
      console.error('Error fetching managers:', error);
    }
  };

  const handleCreateBranch = async (data) => {
    try {
      await createBranch(data);
      toast.success('Branch created successfully');
      setShowCreateModal(false);
      fetchBranches();
      fetchBranchMode();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create branch');
      throw error;
    }
  };

  const handleUpdateBranch = async (id, data) => {
    try {
      await updateBranch(id, data);
      toast.success('Branch updated successfully');
      setShowEditModal(false);
      setSelectedBranch(null);
      fetchBranches();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update branch');
      throw error;
    }
  };

  // Delete functionality moved to BranchDetailPage

  const handleBranchClick = (branch) => {
    navigate(`/branches/${branch.id}`);
  };

  const handleEditClick = (e, branch) => {
    e.stopPropagation();
    setSelectedBranch(branch);
    setShowEditModal(true);
  };

  if (loading) {
    return <LoadingSpinner message="Loading branches..." className="min-h-screen" />;
  }

  return (
    <div className="min-h-screen bg-secondary-offWhite">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-primary-black flex items-center gap-2">
                <Building2 className="h-8 w-8 text-accent-gold" />
                Branch Management
              </h1>
              <p className="mt-1 text-sm text-primary-slate">
                {branchMode?.singleBranchMode
                  ? 'Single branch mode active. Add a second branch to enable multi-branch features.'
                  : `Managing ${branchMode?.branchCount || 0} branches`}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search branches..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent w-48 md:w-64"
                />
              </div>

              {/* View Toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1 border border-gray-200">
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-all ${
                    viewMode === 'list'
                      ? 'bg-white text-primary-black shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  title="List View"
                >
                  <List className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md transition-all ${
                    viewMode === 'grid'
                      ? 'bg-white text-primary-black shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  title="Grid View"
                >
                  <LayoutGrid className="h-5 w-5" />
                </button>
              </div>

              {/* Create Button */}
              {canManageBranches && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors"
                >
                  <Plus className="h-5 w-5" />
                  Add Branch
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Single Branch Mode Message */}
        {branchMode?.singleBranchMode && branches.length <= 1 ? (
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
            <div className="p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <Building2 className="h-10 w-10 text-gray-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Single-Branch Mode Active</h2>
              <p className="text-gray-600 max-w-md mx-auto mb-6">
                Your system is operating in single-branch mode. No branch management is needed.
                Stock is managed directly on products.
              </p>
              {canManageBranches && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-500">
                    To enable multi-branch features, create additional branches.
                  </p>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors"
                  >
                    <Plus className="h-5 w-5" />
                    Add Branch to Enable Multi-Branch Mode
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : branches.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md overflow-hidden text-center py-12">
            <Building2 className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg">No branches found</p>
            <p className="text-gray-400 text-sm mt-2">
              {canManageBranches
                ? 'Click "Add Branch" to create your first branch'
                : 'No branches have been created yet'}
            </p>
          </div>
        ) : viewMode === 'list' ? (
          /* Table View */
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Branch
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Manager
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Staff
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Products
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {branches.map((branch) => (
                    <tr
                      key={branch.id}
                      onClick={() => handleBranchClick(branch)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-lg bg-accent-gold/10 flex items-center justify-center">
                              <Building2 className="h-5 w-5 text-accent-gold" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">
                                {branch.name}
                              </span>
                              {branch.is_main && (
                                <span className="px-2 py-0.5 text-xs bg-accent-gold/20 text-accent-gold rounded-full">
                                  Main
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">{branch.code}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {branch.manager_name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Users className="h-4 w-4" />
                          {branch.staff_count || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Package className="h-4 w-4" />
                          {branch.product_count || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {branch.is_active ? (
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 flex items-center gap-1 w-fit">
                            <CheckCircle className="h-3 w-3" />
                            Active
                          </span>
                        ) : (
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 flex items-center gap-1 w-fit">
                            <XCircle className="h-3 w-3" />
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          {canManageBranches && (
                            <>
                              <button
                                onClick={(e) => handleEditClick(e, branch)}
                                className="text-blue-600 hover:text-blue-900 p-1"
                                title="Edit"
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          <ChevronRight className="h-5 w-5 text-gray-400" />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* Grid View */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {branches.map((branch) => (
              <div
                key={branch.id}
                onClick={() => handleBranchClick(branch)}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer border border-gray-100 group"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-lg bg-accent-gold/10 flex items-center justify-center">
                        <Building2 className="h-6 w-6 text-accent-gold" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-gray-900">{branch.name}</h3>
                          {branch.is_main && (
                            <span className="px-2 py-0.5 text-xs bg-accent-gold/20 text-accent-gold rounded-full">
                              Main
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{branch.code}</p>
                      </div>
                    </div>
                    {branch.is_active ? (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Active
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Inactive
                      </span>
                    )}
                  </div>

                  {/* Branch Info */}
                  <div className="space-y-2 mb-4">
                    {branch.address && (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <MapPin className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{branch.address}</span>
                      </div>
                    )}
                    {branch.phone && (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Phone className="h-4 w-4 flex-shrink-0" />
                        <span>{branch.phone}</span>
                      </div>
                    )}
                    {branch.manager_name && (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Users className="h-4 w-4 flex-shrink-0" />
                        <span>Manager: {branch.manager_name}</span>
                      </div>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Users className="h-4 w-4 text-blue-500" />
                        <span>{branch.staff_count || 0} Staff</span>
                      </div>
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Package className="h-4 w-4 text-green-500" />
                        <span>{branch.product_count || 0} Products</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {canManageBranches && (
                        <>
                          <button
                            onClick={(e) => handleEditClick(e, branch)}
                            className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"
                            title="Edit"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Showing {branches.length} of {pagination.total} branches
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-3 py-1 border rounded-md disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
                disabled={pagination.page >= pagination.totalPages}
                className="px-3 py-1 border rounded-md disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Branch Modal */}
      {showCreateModal && (
        <BranchFormModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateBranch}
          managers={availableManagers}
          title="Create New Branch"
        />
      )}

      {/* Edit Branch Modal */}
      {showEditModal && selectedBranch && (
        <BranchFormModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedBranch(null);
          }}
          onSubmit={(data) => handleUpdateBranch(selectedBranch.id, data)}
          branch={selectedBranch}
          managers={availableManagers}
          title="Edit Branch"
        />
      )}
    </div>
  );
};

// Branch Form Modal Component
const BranchFormModal = ({ isOpen, onClose, onSubmit, branch, managers, title }) => {
  const [formData, setFormData] = useState({
    name: branch?.name || '',
    code: branch?.code || '',
    address: branch?.address || '',
    phone: branch?.phone || '',
    email: branch?.email || '',
    managerId: branch?.manager_id || '',
    isActive: branch?.is_active ?? true,
    isMain: branch?.is_main ?? false
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Branch name is required');
      return;
    }

    setLoading(true);
    try {
      await onSubmit({
        ...formData,
        managerId: formData.managerId || null
      });
    } catch (error) {
      // Error handled in parent
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">{title}</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Branch Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                placeholder="e.g., Main Branch, Victoria Island"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Branch Code</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                placeholder="Auto-generated if left empty (e.g., BR001)"
                disabled={!!branch}
              />
              <p className="text-xs text-gray-500 mt-1">
                Branch code cannot be changed after creation
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <textarea
                value={formData.address}
                onChange={(e) => setFormData((f) => ({ ...f, address: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                placeholder="Branch address"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData((f) => ({ ...f, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                  placeholder="Phone number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData((f) => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                  placeholder="branch@example.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Branch Manager</label>
              <select
                value={formData.managerId}
                onChange={(e) => setFormData((f) => ({ ...f, managerId: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
              >
                <option value="">Select a manager (optional)</option>
                {managers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.full_name} ({m.email})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData((f) => ({ ...f, isActive: e.target.checked }))}
                  className="w-4 h-4 text-accent-gold border-gray-300 rounded focus:ring-accent-gold"
                />
                <span className="text-sm text-gray-700">Active</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isMain}
                  onChange={(e) => setFormData((f) => ({ ...f, isMain: e.target.checked }))}
                  className="w-4 h-4 text-accent-gold border-gray-300 rounded focus:ring-accent-gold"
                />
                <span className="text-sm text-gray-700">Main Branch (HQ)</span>
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : branch ? 'Update Branch' : 'Create Branch'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BranchesPage;
